import React, { useState, useEffect } from "react";
import * as XLSX from "xlsx";

import Layout from "../components/Layout";
import api from "../api";

export default function Pairs() {
  const [event, setEvent] = useState(null);
  const [players, setPlayers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [extraGroups, setExtraGroups] = useState([]);
  const [dragged, setDragged] = useState(null);
  const [dragOverKey, setDragOverKey] = useState(null);
  const [firstTeeTime, setFirstTeeTime] = useState("09:00");
  const [teeInterval, setTeeInterval] = useState(8);
  const [allSocietyPlayers, setAllSocietyPlayers] = useState([]);
  const [replacingId, setReplacingId] = useState(null);
  const [replaceBusy, setReplaceBusy] = useState(false);
  const [snapshot, setSnapshot] = useState({ exists: false });
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [ghost, setGhost] = useState(null);
  const [ghostEligible, setGhostEligible] = useState(false);
  const [ghostBusy, setGhostBusy] = useState(false);
  const [ghostMsg, setGhostMsg] = useState("");
  const [pairing, setPairing] = useState(false);
  // Whether this event has real named teams set up on the Teams page
  // (inter-club Better Ball/Fourball/Foursomes/Greensomes Match Play).
  // When true, team_id means "which club side" and must never be written
  // by this page — only grp/pair_slot. When false, team_id is this page's
  // own ad-hoc pairing id, fully managed here as before. Mixing the two up
  // silently overwrote a real player's club assignment — found live
  // 2026-09-06 (see [[gsm_teams_pairs_team_id_conflict]]).
  const [namedTeamsExist, setNamedTeamsExist] = useState(false);
  const [teamNameMap, setTeamNameMap] = useState({});

  useEffect(() => { loadAll(); loadAllSocietyPlayers(); }, []);

  const loadAll = async () => {
    try {
      const [groupRes, teamsRes] = await Promise.all([
        api.get("/groups"),
        api.get("/teams").catch(() => ({ data: { teams: [] } })),
      ]);
      setEvent(groupRes.data.event);
      if (groupRes.data.event?.first_tee_time) setFirstTeeTime(groupRes.data.event.first_tee_time);
      if (groupRes.data.event?.tee_interval_minutes != null) setTeeInterval(groupRes.data.event.tee_interval_minutes);
      setPlayers((groupRes.data.players || []).filter(p => p.playing));
      setGhost(groupRes.data.ghost || null);
      setGhostEligible(!!groupRes.data.ghostEligible);
      setSnapshot(groupRes.data.snapshot || { exists: false });
      const teams = teamsRes.data.teams || [];
      setNamedTeamsExist(teams.length > 0);
      const nameMap = {};
      teams.forEach(t => { nameMap[t.id] = t.team_name; });
      setTeamNameMap(nameMap);
    } catch(e) { setError("Failed to load pairs"); }
    finally { setLoading(false); }
  };

  // Shows which club/side a player is on when this event has named teams —
  // without this, an inter-club match play organiser has no way to tell
  // players apart when building pairs on this page at all. Mike flagged
  // this live 2026-09-06: "Teams have not made their way to pairs."
  const teamLabel = (player) => namedTeamsExist && teamNameMap[player.team_id]
    ? <span style={{ color: "#7c3aed", fontWeight: 700 }}> [{teamNameMap[player.team_id]}]</span>
    : null;

  const loadAllSocietyPlayers = async () => {
    try {
      const res = await api.get("/players");
      setAllSocietyPlayers(res.data || []);
    } catch(e) { console.error("Load society players:", e.message); }
  };

  // First group tees off at firstTeeTime; each subsequent group follows at
  // teeInterval minutes later. Ported from Groups.jsx 2026-09-06 — pair
  // formats no longer manage tee times there (see Groups.jsx: greyed out
  // for pair formats, this page is now the one place that sets them).
  const addMinutesToTime = (timeStr, minutesToAdd) => {
    const [h, m] = (timeStr || "09:00").split(":").map(Number);
    let total = ((h || 0) * 60 + (m || 0) + minutesToAdd) % 1440;
    if (total < 0) total += 1440;
    const hh = Math.floor(total / 60).toString().padStart(2, "0");
    const mm = (total % 60).toString().padStart(2, "0");
    return `${hh}:${mm}`;
  };
  const teeTimeForGroup = (grp) => addMinutesToTime(firstTeeTime, ((grp || 1) - 1) * (parseInt(teeInterval) || 0));

  const saveTeeTimes = async (newFirst, newInterval) => {
    if (!event) return;
    try {
      await api.put(`/events/${event.id}/teetimes`, { first_tee_time: newFirst, tee_interval_minutes: parseInt(newInterval) || 0 });
    } catch(e) { console.error("Save tee times:", e.message); }
  };

  // A player only counts as "placed" once they're actually in a pair SLOT
  // on this page — signalled by pair_slot, never by team_id alone. team_id
  // is shared with the Teams page (inter-club side assignment for Better
  // Ball/Fourball/Foursomes/Greensomes Match Play — see [[gsm_teams_pairs_team_id_conflict]]),
  // so a player can legitimately have a team_id (their club) without ever
  // having been dragged into a pair here. Using team_id as the placement
  // signal made every Teams-assigned player vanish off this page entirely —
  // found live 2026-09-06 on a real inter-club Better Ball Match Play test.
  // Grouped by club when this event has named teams, so US/EU players sit
  // together and are easy to pair correctly rather than scattered by name.
  const unassignedPlayers = players.filter(p => !p.pair_slot)
    .slice()
    .sort((a, b) => namedTeamsExist ? (a.team_id || 0) - (b.team_id || 0) : 0);
  const groupNumbers = [...new Set([
    ...players.filter(p => p.pair_slot).map(p => p.grp),
    ...extraGroups,
  ])].sort((a, b) => a - b);

  const nextTeamId = () => {
    const used = players.map(p => p.team_id).filter(Boolean);
    return (used.length ? Math.max(...used) : 0) + 1;
  };

  // Same allowance rules already used on the real scoring screens
  // (BetterBallScoreEntry.jsx / MatchPlayScoreEntry.jsx / TeamSingleBallScoreEntry.jsx)
  // — this page never calculates a real score, but showing raw handicap
  // with no adjustment reads as if the allowance isn't applied anywhere at
  // all, which Mike flagged live on a real Better Ball Medal event
  // (showing hcp 21/23/33/27 with no hint of the 85% they actually play off).
  const INDIVIDUAL_PCT_FORMATS = {
    "Better Ball Stableford": 0.85, "Better Ball Medal": 0.85, "Better Ball Stroke Play": 0.85,
    "Fourball Better Ball Stableford": 0.85, "Fourball Stroke Play": 0.85,
    "Better Ball Match Play": 0.9, "Fourball Match Play": 0.9,
  };
  const COMBINED_PAIR_FORMATS = new Set([
    "Foursomes Stableford", "Foursomes Medal", "Foursomes Match Play",
    "Greensomes Stableford", "Greensomes Medal", "Greensomes Match Play",
  ]);
  const isGreensomes = event?.game_type?.startsWith("Greensomes");
  const individualPct = INDIVIDUAL_PCT_FORMATS[event?.game_type];

  // Foursomes/Greensomes don't have a per-player playing handicap at all —
  // it's one shared allowance for the whole pair (50% combined, or 60/40
  // split by whoever's lower) — so that gets shown once per pair, not
  // repeated per player.
  const pairAllowance = (a, b) => {
    if (!a || !b || !event) return null;
    const ha = (a.hcp_adj ?? a.handicap) || 0, hb = (b.hcp_adj ?? b.handicap) || 0;
    if (isGreensomes) return Math.round(0.6 * Math.min(ha, hb) + 0.4 * Math.max(ha, hb));
    return Math.round((ha + hb) * 0.5);
  };

  // Match play plays off the LOWEST Playing Handicap in the match — everyone
  // else gets the difference, not their own raw Playing Handicap. Showing
  // "→ 19 playing" with no baseline reads as if 19 is how many strokes that
  // player gets, which is only true for whoever happens to be lowest. Same
  // bug already fixed on MatchPlayScoreEntry.jsx (see
  // [[gsm_matchplay_allowance_display_relative]]) but missed here — flagged
  // live 2026-09-06. Before a group has all its players in place there's no
  // real match to compute a baseline against, so this only ever applies once
  // a group is complete (4 players for fourball, both pairs for alternate).
  const isFourballMatchPlay = individualPct === 0.9;
  const isAlternateMatchPlay = COMBINED_PAIR_FORMATS.has(event?.game_type) && (event?.game_type || "").endsWith("Match Play");

  // Signed-Up list preview: as soon as match play players are sat in the
  // pool (before anyone's been dragged into a slot), show each one's
  // strokes relative to the lowest PH in that pool rather than just their
  // own raw PH in isolation -- matches GSM_Handicap_Allowance_Audit.docx's
  // own worked example (lowest plays scratch, everyone else gets the
  // difference). Once players are dragged into an actual group, the
  // per-group fourballBaseline further down takes over and is
  // authoritative; this is only a preview for the still-unassigned pool.
  // Found live 2026-09-08 -- flagged by Mike against the audit doc.
  const signedUpBaseline = (isFourballMatchPlay && unassignedPlayers.length > 0)
    ? Math.min(...unassignedPlayers.map(p => Math.round(((p.hcp_adj ?? p.handicap) || 0) * individualPct)))
    : null;

  const playingHcpDisplay = (player, baseline) => {
    if (!individualPct) return null;
    const ph = Math.round(((player.hcp_adj ?? player.handicap) || 0) * individualPct);
    if (baseline === null || baseline === undefined) {
      return <> &times; {Math.round(individualPct * 100)}% &rarr; <b>{ph} HCP Allowance</b></>;
    }
    const strokes = Math.max(0, ph - baseline);
    return <> &times; {Math.round(individualPct * 100)}% = HCP Allowance {ph}
      {strokes === 0 ? <span style={{ color: "#166534", fontWeight: 700 }}> (scratch)</span> : <> &rarr; <b>{strokes} strokes</b></>}
    </>;
  };

  const pairAllowanceDisplay = (allowance, baseline) => {
    if (allowance === null) return null;
    if (baseline === null || baseline === undefined) {
      return <span style={{ fontWeight: 400, color: "#4b5563" }}> — combined HCP Allowance: {allowance}</span>;
    }
    const strokes = Math.max(0, allowance - baseline);
    return <span style={{ fontWeight: 400, color: "#4b5563" }}>
      {" "}— combined HCP Allowance: {allowance}{strokes === 0 ? <span style={{ color: "#166534", fontWeight: 700 }}> (scratch)</span> : <> &rarr; {strokes} strokes</>}
    </span>;
  };

  // Which physical slot (Pair 1 vs Pair 2) a team occupies is read directly
  // from pair_slot, never inferred from the team_id value — team_id is just
  // an arbitrary incrementing id, and sorting by it used to mean whichever
  // team happened to get the smaller id always rendered as Pair 1 regardless
  // of which slot the organiser actually dropped into. Mike hit this live
  // 2026-09-06: dropping the first player of a new group straight into a
  // Pair 2 slot, they'd render under Pair 1 instead — "using some form of
  // default". pair_slot fixes that by recording the real slot directly.
  const getSlots = (grp) => {
    const members = players.filter(p => p.grp === grp && p.pair_slot);
    const pair1 = members.filter(p => p.pair_slot === 1);
    const pair2 = members.filter(p => p.pair_slot === 2);
    return {
      pair1_a: pair1[0] || null, pair1_b: pair1[1] || null,
      pair2_a: pair2[0] || null, pair2_b: pair2[1] || null,
      tid1: pair1[0]?.team_id || null, tid2: pair2[0]?.team_id || null,
    };
  };

  // Every drag-and-drop action below writes straight through to
  // /groups/save immediately (no separate Save step) -- this is the same
  // model the Groups page's own drag-and-drop already uses, so an organiser
  // building groups here and adjusting them there never has to remember to
  // save one before switching to the other.
  // `from` is either "pool" (player dragged from Signed Up) or { grp, slot }
  // (player dragged out of another slot). When the target slot is already
  // occupied and the player came from another slot, the occupant swaps into
  // the player's old slot rather than getting bumped back to Signed Up —
  // Mike flagged live 2026-09-06 that moving someone into an occupied slot
  // should swap, not evict, whoever was there.
  const assignToSlot = async (player, grp, slotKey, from) => {
    const slots = getSlots(grp);
    const isPair1 = slotKey === "pair1_a" || slotKey === "pair1_b";
    const pairSlot = isPair1 ? 1 : 2;
    let teamId = isPair1 ? slots.tid1 : slots.tid2;
    // Inter-club: team_id is the player's own club, already set on the
    // Teams page — never generate or transfer one here. Ad-hoc: this page
    // owns team_id fully, exactly as before.
    if (!teamId) teamId = namedTeamsExist ? player.team_id : nextTeamId();
    const occupant = slots[slotKey];
    const swapping = occupant && occupant.id !== player.id && from && from.grp !== undefined;
    const fromPairSlot = swapping && (from.slot === "pair1_a" || from.slot === "pair1_b") ? 1 : 2;

    const updates = [{ id: player.id, grp, bingo_number: player.bingo_number, team_id: teamId, pair_slot: pairSlot }];
    if (occupant && occupant.id !== player.id) {
      updates.push(swapping
        ? { id: occupant.id, grp: from.grp, bingo_number: occupant.bingo_number, team_id: namedTeamsExist ? occupant.team_id : player.team_id, pair_slot: fromPairSlot }
        : { id: occupant.id, grp: 0, bingo_number: occupant.bingo_number, team_id: namedTeamsExist ? occupant.team_id : null, pair_slot: null });
    }
    try {
      await api.post("/groups/save", { groups: updates });
      setPlayers(prev => prev.map(p => {
        if (p.id === player.id) return { ...p, grp, team_id: teamId, pair_slot: pairSlot };
        if (occupant && p.id === occupant.id) {
          return swapping
            ? { ...p, grp: from.grp, team_id: namedTeamsExist ? p.team_id : player.team_id, pair_slot: fromPairSlot }
            : { ...p, grp: 0, team_id: namedTeamsExist ? p.team_id : null, pair_slot: null };
        }
        return p;
      }));
      setExtraGroups(prev => prev.filter(g => g !== grp));
    } catch(e) { console.error(e); setError("Failed to assign player"); }
  };

  const unassignPlayer = async (player) => {
    try {
      const team_id = namedTeamsExist ? player.team_id : null;
      await api.post("/groups/save", { groups: [{ id: player.id, grp: 0, bingo_number: player.bingo_number, team_id, pair_slot: null }] });
      setPlayers(prev => prev.map(p => p.id === player.id ? { ...p, grp: 0, team_id, pair_slot: null } : p));
    } catch(e) { console.error(e); setError("Failed to unassign player"); }
  };

  const handleDragStart = (e, player, from) => {
    e.stopPropagation();
    setDragged({ player, from });
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragEnd = (e) => { e.stopPropagation(); setDragged(null); setDragOverKey(null); };
  const handleDragOver = (e, key) => {
    e.preventDefault(); e.stopPropagation();
    if (dragOverKey !== key) setDragOverKey(key);
  };
  const handleDragLeave = (e) => { e.stopPropagation(); setDragOverKey(null); };
  const handleDropOnSlot = (e, grp, slotKey) => {
    e.preventDefault(); e.stopPropagation();
    setDragOverKey(null);
    if (dragged) assignToSlot(dragged.player, grp, slotKey, dragged.from);
    setDragged(null);
  };
  const handleDropOnPool = (e) => {
    e.preventDefault(); e.stopPropagation();
    setDragOverKey(null);
    if (dragged && dragged.from !== "pool") unassignPlayer(dragged.player);
    setDragged(null);
  };

  const addGroup = () => {
    const next = (groupNumbers.length ? Math.max(...groupNumbers) : 0) + 1;
    setExtraGroups(prev => [...prev, next]);
  };

  // Auto Pair — 2026-10-21: reverses the earlier "pairs are manual only"
  // decision at Mike's explicit request, covering both 2-ball (Foursomes/
  // Greensomes) and 4-ball (Better Ball/Fourball) pair formats in one pass.
  // A full reshuffle, same as Groups.jsx's own Auto Group — confirmed first
  // since it replaces whatever's currently on screen, including any manual
  // drag-and-drop arrangement.
  const autoPair = async () => {
    if (!window.confirm("Auto Pair everyone currently Playing, balanced by handicap? This replaces the current pairs/groups on screen.")) return;
    setPairing(true);
    setError("");
    try {
      const res = await api.post("/groups/autopair");
      setPlayers((res.data.players || []).filter(p => p.playing));
      setExtraGroups([]);
    } catch(e) { setError(e.response?.data?.error || "Failed to auto-pair"); }
    finally { setPairing(false); }
  };

  // Buggy toggle, drop-out, and substitute-in — ported from Groups.jsx
  // 2026-09-06 so pair-format organisers keep these once Groups is greyed
  // out for their format. Same underlying /events/:id/players upsert Groups
  // and Players both already use, so all three screens stay in agreement.
  const updateBuggy = async (player, value) => {
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: player.player_id, playing: player.playing, buggy: value, captain: player.captain, vice_captain: player.vice_captain, hcp_review: player.hcp_review || false,
      });
      setPlayers(prev => prev.map(p => p.id === player.id ? { ...p, buggy: value } : p));
    } catch(e) { console.error("Update buggy:", e.message); }
  };

  const removePlayer = async (player) => {
    if (!window.confirm(`Remove ${player.name} from today's event? This unticks them as Playing — they can be added back from the Players page.`)) return;
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: player.player_id, playing: false, buggy: player.buggy, captain: player.captain, vice_captain: player.vice_captain, hcp_review: player.hcp_review || false,
      });
      setPlayers(prev => prev.filter(p => p.id !== player.id));
    } catch(e) { console.error("Remove player:", e.message); setError("Failed to remove player"); }
  };

  // Late substitute — keeps the same group AND the same pair slot the
  // outgoing player had, so a drop-out minutes before the tee doesn't
  // require rebuilding that pair by hand.
  const replacePlayer = async (oldPlayer, newPlayerId) => {
    setReplaceBusy(true);
    try {
      await api.post("/events/" + event.id + "/players", {
        player_id: newPlayerId, playing: true, buggy: false, captain: false, vice_captain: false, hcp_review: false,
      });
      await api.post("/events/" + event.id + "/players", {
        player_id: oldPlayer.player_id, playing: false, buggy: oldPlayer.buggy, captain: oldPlayer.captain,
        vice_captain: oldPlayer.vice_captain, hcp_review: oldPlayer.hcp_review || false,
      });
      const res = await api.get("/groups");
      const newEp = (res.data.players || []).find(p => p.player_id === newPlayerId);
      if (newEp && oldPlayer.grp && oldPlayer.team_id) {
        await api.post("/groups/save", { groups: [{ id: newEp.id, grp: oldPlayer.grp, bingo_number: oldPlayer.bingo_number, team_id: oldPlayer.team_id, pair_slot: oldPlayer.pair_slot }] });
      }
      setReplacingId(null);
      await loadAll();
    } catch(e) { console.error("Replace player:", e.message); setError("Failed to replace player"); }
    finally { setReplaceBusy(false); }
  };

  // "Get out of jail card" — ported from Groups.jsx 2026-09-06, now
  // includes pair_slot so restoring a saved pair-format checkpoint doesn't
  // collapse everyone back to Pair 1 (see getSlots comment above).
  const saveGroups = async () => {
    try {
      const res = await api.post("/groups/snapshot");
      setSnapshot({ exists: true, created_at: res.data.created_at });
      alert("Groups saved! You can restore this exact setup later with Restore Last Group.");
    } catch(e) { setError("Failed to save groups"); }
  };
  const restoreLastGroup = async () => {
    if (!window.confirm("Restore the last saved group setup? This replaces everything on screen now, including any changes made since you last clicked Save Groups.")) return;
    setSnapshotBusy(true);
    try {
      const res = await api.post("/groups/snapshot/restore");
      setPlayers((res.data.players || []).filter(p => p.playing));
    } catch(e) { setError("Failed to restore: " + (e.response?.data?.error || e.message)); }
    finally { setSnapshotBusy(false); }
  };

  const addGhost = async () => {
    setGhostBusy(true); setGhostMsg("");
    try {
      const res = await api.post("/groups/add-ghost");
      setGhost(res.data.ghost);
      const groups = res.data.threeballGroups || [];
      setGhostMsg(groups.length
        ? `${res.data.ghost.name} (HCP ${res.data.ghost.handicap}) is the ghost — filling group${groups.length !== 1 ? "s" : ""} ${groups.join(", ")}`
        : `${res.data.ghost.name} (HCP ${res.data.ghost.handicap}) is the ghost, but no groups are currently stuck at 3.`);
      setTimeout(() => setGhostMsg(""), 6000);
    } catch(e) { setError("Failed to set ghost: " + (e.response?.data?.error || e.message)); }
    finally { setGhostBusy(false); }
  };
  const removeGhost = async () => {
    setGhostBusy(true); setGhostMsg("");
    try {
      await api.post("/groups/remove-ghost");
      setGhost(null);
      setGhostMsg("Ghost removed");
      setTimeout(() => setGhostMsg(""), 3000);
    } catch(e) { setError("Failed to remove ghost: " + (e.response?.data?.error || e.message)); }
    finally { setGhostBusy(false); }
  };

  const exportToExcel = () => {
    const rows = [];
    groupNumbers.forEach(grp => {
      const slots = getSlots(grp);
      [1, 2].forEach(pairNum => {
        const [a, b] = pairNum === 1 ? [slots.pair1_a, slots.pair1_b] : [slots.pair2_a, slots.pair2_b];
        [a, b].forEach(p => {
          if (!p) return;
          rows.push({
            "Member": p.name + (p.captain ? " (C)" : ""),
            "WHS HCP": p.handicap,
            "Course HCP": p.hcp_adj ?? p.handicap,
            "Group": grp,
            "Pair": pairNum,
            "Tee Off": teeTimeForGroup(grp),
            "Buggy": p.buggy ? "Yes" : "No",
          });
        });
      });
    });
    if (rows.length === 0) { setError("No players to export"); return; }
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, XLSX.utils.json_to_sheet(rows), "Pairs");
    XLSX.writeFile(wb, "Pairs.xlsx");
  };

  const slotBoxStyle = (key, occupied) => ({
    border: "2px dashed " + (dragOverKey === key ? "#60a5fa" : (occupied ? "transparent" : "#d1d5db")),
    background: dragOverKey === key ? "#eff6ff" : (occupied ? "white" : "transparent"),
    borderRadius: "4px", padding: "6px 8px", marginBottom: "6px", minHeight: "32px",
    display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "13px", gap: "6px",
  });

  const captainBadge = (p) => p.captain
    ? <span style={{ background: "#facc15", color: "#78350f", fontSize: "9px", fontWeight: 700, padding: "0 4px", borderRadius: "3px", marginLeft: "3px" }}>C</span>
    : null;

  const substituteSelect = (target) => (
    <span style={{ display: "flex", alignItems: "center", gap: "6px", flexWrap: "wrap" }}>
      <span style={{ fontSize: "11px", color: "#6b7280" }}>Replace with:</span>
      <select autoFocus disabled={replaceBusy}
        onChange={e => { if (e.target.value) replacePlayer(target, parseInt(e.target.value)); }}
        style={{ fontSize: "11px", border: "1px solid #d1d5db", borderRadius: "4px", padding: "2px 4px" }}>
        <option value="">Choose a substitute...</option>
        {allSocietyPlayers
          .filter(sp => !players.some(pp => pp.player_id === sp.id))
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(sp => <option key={sp.id} value={sp.id}>{sp.name} (hcp {sp.handicap})</option>)}
      </select>
      <button onClick={() => setReplacingId(null)} disabled={replaceBusy}
        style={{ fontSize: "11px", color: "#9ca3af", border: "none", background: "none", cursor: "pointer" }}>Cancel</button>
    </span>
  );

  return (
    <Layout>
      <style>{`
        .print-only { display: none; }
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          @page { margin: 15mm; }
        }
      `}</style>
      <div className="print-only">
        <h1 style={{ fontSize: "20px", fontWeight: 700, marginBottom: "2px" }}>{event?.venue || "Pairs"}</h1>
        <div style={{ fontSize: "12px", color: "#555", marginBottom: "18px" }}>
          {event?.date ? new Date(event.date).toLocaleDateString("en-GB") : ""}{event?.game_type ? " — " + event.game_type : ""}
        </div>
        {groupNumbers.map(grp => {
          const slots = getSlots(grp);
          return (
            <div key={grp} style={{ marginBottom: "18px", pageBreakInside: "avoid" }}>
              <div style={{ fontSize: "16px", fontWeight: 700, background: "#166534", color: "white", padding: "5px 10px", marginBottom: "4px" }}>
                Group {grp} — Tee Off {teeTimeForGroup(grp)}
              </div>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                <tbody>
                  {[1, 2].map(pairNum => {
                    const [a, b] = pairNum === 1 ? [slots.pair1_a, slots.pair1_b] : [slots.pair2_a, slots.pair2_b];
                    return [a, b].filter(Boolean).map(p => (
                      <tr key={p.id} style={{ borderBottom: "1px solid #ddd" }}>
                        <td style={{ padding: "5px 8px", width: "50px", color: "#166534", fontWeight: 700 }}>Pair {pairNum}</td>
                        <td style={{ padding: "5px 8px" }}>{p.name}{p.captain ? " (C)" : ""}</td>
                        <td style={{ padding: "5px 8px", textAlign: "center", width: "70px" }}>HCP {p.hcp_adj ?? p.handicap}</td>
                        <td style={{ padding: "5px 8px", textAlign: "center", width: "50px" }}>{p.buggy ? "🚗" : ""}</td>
                      </tr>
                    ));
                  })}
                </tbody>
              </table>
            </div>
          );
        })}
        {groupNumbers.length === 0 && <div style={{ color: "#888" }}>No groups assigned yet.</div>}
      </div>

      <div className="no-print" style={{ padding: "16px", maxWidth: "1100px", margin: "0 auto" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "16px" }}>
          <h2 style={{ fontSize: "18px", fontWeight: "700", color: "#14532d" }}>🤝 Pairs</h2>
          {event && <span style={{ fontSize: "13px", color: "#6b7280" }}>{event.game_type}</span>}
        </div>

        <div style={{ backgroundColor: "#f3f4f6", border: "1px solid #e5e7eb", borderRadius: "8px", padding: "10px 14px", marginBottom: "12px", display: "flex", alignItems: "center", gap: "14px", flexWrap: "wrap", fontSize: "12px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
            <label style={{ color: "#374151", fontWeight: 600 }}>First tee</label>
            <input type="time" value={firstTeeTime}
              onChange={e => { setFirstTeeTime(e.target.value); saveTeeTimes(e.target.value, teeInterval); }}
              style={{ border: "1px solid #d1d5db", borderRadius: "4px", padding: "3px 6px" }} />
            <label style={{ color: "#374151", fontWeight: 600, marginLeft: "4px" }}>Interval</label>
            <input type="number" min="1" value={teeInterval}
              onChange={e => { setTeeInterval(e.target.value); saveTeeTimes(firstTeeTime, e.target.value); }}
              style={{ border: "1px solid #d1d5db", borderRadius: "4px", padding: "3px 6px", width: "50px" }} />
            <span style={{ color: "#6b7280" }}>mins</span>
          </div>
          <div style={{ display: "flex", gap: "6px", flexWrap: "wrap", marginLeft: "auto" }}>
            <button onClick={saveGroups} style={{ background: "#16a34a", color: "white", border: "none", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>Save Groups</button>
            <button onClick={restoreLastGroup} disabled={!snapshot.exists || snapshotBusy}
              title={snapshot.exists ? "Last saved " + new Date(snapshot.created_at).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' }) : "No saved checkpoint yet — click Save Groups first"}
              style={{ background: snapshot.exists ? "#d97706" : "#d1d5db", color: "white", border: "none", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: snapshot.exists ? "pointer" : "not-allowed" }}>
              {snapshotBusy ? "Restoring..." : "Restore Last Group"}
            </button>
            <button onClick={exportToExcel} style={{ background: "#059669", color: "white", border: "none", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>📊 Export Excel</button>
            <button onClick={async () => {
              if (window.electronAPI?.printPreview) {
                const result = await window.electronAPI.printPreview();
                if (!result?.success) alert("Print preview failed: " + (result?.error || "unknown error"));
              } else {
                window.print();
              }
            }} style={{ background: "#475569", color: "white", border: "none", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>🖨️ Print</button>
            {ghostEligible && (
              <button onClick={addGhost} disabled={ghostBusy} style={{ background: "#334155", color: "white", border: "none", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>👻 Ghost</button>
            )}
            {ghostEligible && ghost && (
              <button onClick={removeGhost} disabled={ghostBusy} style={{ background: "white", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "5px", padding: "6px 12px", fontWeight: 700, cursor: "pointer" }}>Remove Ghost</button>
            )}
          </div>
        </div>

        {ghostMsg && <div style={{ background: "#f1f5f9", color: "#334155", padding: "8px 12px", fontSize: "13px", fontWeight: 600, borderRadius: "6px", marginBottom: "10px" }}>👻 {ghostMsg}</div>}
        {ghostEligible && ghost && !ghostMsg && (
          <div style={{ background: "#f8fafc", color: "#475569", padding: "8px 12px", fontSize: "12px", borderRadius: "6px", marginBottom: "10px" }}>
            👻 Ghost: <strong>{ghost.name}</strong> (HCP {ghost.handicap}) — filling any group stuck at 3
          </div>
        )}

        <div style={{ backgroundColor: "#e0f2fe", border: "1px solid #bae6fd", borderRadius: "8px", padding: "10px 14px", marginBottom: "16px", fontSize: "13px", color: "#0369a1" }}>
          Drag a player from the Signed Up list on the left into a Pair slot on the right to build groups and pairs by hand. Drag a player out of a slot (or click ✕) to send them back to Signed Up.
        </div>

        {loading && <div style={{ textAlign: "center", padding: "32px", color: "#6b7280" }}>Loading...</div>}
        {error && <div style={{ color: "#dc2626", padding: "12px" }}>{error}</div>}

        {!loading && (
          <div style={{ display: "flex", gap: "20px", alignItems: "flex-start" }}>
            <div style={{ flex: "0 0 240px", position: "sticky", top: "16px" }}>
              <div style={{ fontWeight: 700, fontSize: "13px", color: "#374151", marginBottom: "6px" }}>Signed Up for the Match ({unassignedPlayers.length})</div>
              <div
                onDragOver={(e) => handleDragOver(e, "pool")}
                onDragLeave={handleDragLeave}
                onDrop={handleDropOnPool}
                style={{
                  border: "2px dashed " + (dragOverKey === "pool" ? "#60a5fa" : "#d1d5db"),
                  background: dragOverKey === "pool" ? "#eff6ff" : "transparent",
                  borderRadius: "8px", padding: "10px", display: "flex", flexDirection: "column", gap: "8px", minHeight: "56px",
                }}>
                {unassignedPlayers.map(p => (
                  <div key={p.id} style={{ background: "#f9fafb", border: "1px solid #e5e7eb", borderRadius: "6px", padding: "6px 10px", fontSize: "13px" }}>
                    {replacingId === p.id ? substituteSelect(p) : (
                      <div draggable onDragStart={(e) => handleDragStart(e, p, "pool")} onDragEnd={handleDragEnd}
                        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "6px", cursor: "grab" }}>
                        <span>
                          ⠿ {p.name}{captainBadge(p)}{teamLabel(p)} <span style={{ color: "#9ca3af" }}>
                            (Course HCP {p.hcp_adj ?? p.handicap}{playingHcpDisplay(p, signedUpBaseline)})
                          </span>
                        </span>
                        <span style={{ display: "flex", alignItems: "center", gap: "4px" }}>
                          <input type="checkbox" checked={!!p.buggy} onChange={e => updateBuggy(p, e.target.checked)} title="Buggy" style={{ cursor: "pointer" }} />
                          <button onClick={() => setReplacingId(p.id)} title="Replace with a substitute"
                            style={{ color: "#93c5fd", fontWeight: 700, border: "none", background: "none", cursor: "pointer", fontSize: "12px" }}>🔁</button>
                          <button onClick={() => removePlayer(p)} title="Remove from today's event"
                            style={{ color: "#fca5a5", fontWeight: 700, border: "none", background: "none", cursor: "pointer" }}>✕</button>
                        </span>
                      </div>
                    )}
                  </div>
                ))}
                {unassignedPlayers.length === 0 && <div style={{ color: "#9ca3af", fontSize: "13px", fontStyle: "italic" }}>Everyone's assigned to a pair</div>}
              </div>
            </div>

            <div style={{ flex: "1 1 auto", minWidth: 0 }}>
              {groupNumbers.map(grp => {
                const slots = getSlots(grp);
                const allFour = [slots.pair1_a, slots.pair1_b, slots.pair2_a, slots.pair2_b];
                const groupComplete = allFour.every(Boolean);
                const fourballBaseline = (isFourballMatchPlay && groupComplete)
                  ? Math.min(...allFour.map(p => Math.round(((p.hcp_adj ?? p.handicap) || 0) * individualPct)))
                  : null;
                const alternateAllowances = groupComplete && isAlternateMatchPlay
                  ? [pairAllowance(slots.pair1_a, slots.pair1_b), pairAllowance(slots.pair2_a, slots.pair2_b)]
                  : null;
                const alternateBaseline = alternateAllowances ? Math.min(...alternateAllowances) : null;
                return (
                  <div key={grp} style={{ border: "1px solid #d1d5db", borderRadius: "8px", marginBottom: "12px", overflow: "hidden" }}>
                    <div style={{ backgroundColor: "#f3f4f6", padding: "8px 14px", fontWeight: "700", fontSize: "14px", color: "#374151", display: "flex", justifyContent: "space-between" }}>
                      <span>Group {grp}</span>
                      <span style={{ fontWeight: 400, color: "#6b7280" }}>Tee Off {teeTimeForGroup(grp)}</span>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: "12px", padding: "12px 14px" }}>
                      {[1, 2].map(pairNum => {
                        const slotKeys = pairNum === 1 ? ["pair1_a", "pair1_b"] : ["pair2_a", "pair2_b"];
                        const [slotA, slotB] = slotKeys;
                        const allowance = COMBINED_PAIR_FORMATS.has(event?.game_type)
                          ? pairAllowance(slots[slotA], slots[slotB]) : null;
                        return (
                          <div key={pairNum} style={{ flex: "1 1 280px", border: "1px solid #bbf7d0", backgroundColor: "#f0fdf4", borderRadius: "6px", padding: "10px 12px" }}>
                            <div style={{ fontSize: "12px", fontWeight: "700", color: "#166534", marginBottom: "6px" }}>
                              Pair {pairNum}
                              {pairAllowanceDisplay(allowance, isAlternateMatchPlay ? alternateBaseline : null)}
                            </div>
                            {slotKeys.map(slotKey => {
                              const occupant = slots[slotKey];
                              const key = grp + ":" + slotKey;
                              return (
                                <div key={slotKey}
                                  onDragOver={(e) => handleDragOver(e, key)}
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) => handleDropOnSlot(e, grp, slotKey)}
                                  style={slotBoxStyle(key, !!occupant)}>
                                  {occupant && replacingId === occupant.id ? substituteSelect(occupant) : (
                                    <>
                                      {occupant ? (
                                        <span draggable
                                          onDragStart={(e) => handleDragStart(e, occupant, { grp, slot: slotKey })}
                                          onDragEnd={handleDragEnd}
                                          style={{ cursor: "grab", display: "flex", alignItems: "center", gap: "4px" }}>
                                          ⠿ {occupant.name}{captainBadge(occupant)}{teamLabel(occupant)} <span style={{ color: "#6b7280" }}>
                                            ({occupant.hcp_adj ?? occupant.handicap}{playingHcpDisplay(occupant, fourballBaseline)})
                                          </span>{occupant.buggy ? " 🛺" : ""}
                                        </span>
                                      ) : <span style={{ color: "#9ca3af", fontStyle: "italic" }}>Drop player here</span>}
                                      {occupant && (
                                        <span style={{ display: "flex", alignItems: "center", gap: "3px" }}>
                                          <input type="checkbox" checked={!!occupant.buggy} onChange={e => updateBuggy(occupant, e.target.checked)} title="Buggy" style={{ cursor: "pointer" }} />
                                          <button onClick={() => setReplacingId(occupant.id)} title="Replace with a substitute"
                                            style={{ color: "#93c5fd", fontWeight: 700, border: "none", background: "none", cursor: "pointer", fontSize: "11px" }}>🔁</button>
                                          <button onClick={() => unassignPlayer(occupant)} title="Back to Signed Up"
                                            style={{ color: "#fca5a5", fontWeight: 700, border: "none", background: "none", cursor: "pointer" }}>✕</button>
                                        </span>
                                      )}
                                    </>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}

              <button onClick={addGroup}
                style={{ background: "#dbeafe", color: "#1d4ed8", border: "none", borderRadius: "6px", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: "pointer" }}>
                + Add Group
              </button>
              <button onClick={autoPair} disabled={pairing} title="Automatically pair everyone currently Playing, balanced by handicap"
                style={{ background: "#2563eb", color: "white", border: "none", borderRadius: "6px", padding: "8px 14px", fontSize: "13px", fontWeight: 700, cursor: pairing ? "default" : "pointer", opacity: pairing ? 0.7 : 1, marginLeft: "8px" }}>
                {pairing ? "Pairing..." : "⚡ Auto Pair"}
              </button>

              {groupNumbers.length === 0 && unassignedPlayers.length === 0 && (
                <div style={{ textAlign: "center", padding: "32px", color: "#6b7280" }}>No playing players — go to the Players screen first.</div>
              )}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
